// Generic count-up animation for numeric stat elements (e.g. "97%", "54%", "42"). Scoped
// per "counter container" so this one file can be reused by any section — native or
// custom — that wants numbers to count up from 0 when scrolled into view, instead of
// duplicating the animation logic in every section that needs it.
//
// A counter container is discovered one of two ways:
//   1. An element carrying [data-counter-scope] IS the container. Use this directly on
//      any custom section's own wrapper (e.g. stats-citations.liquid).
//   2. An element carrying [data-counter-marker] is a placeholder inside a native/locked
//      section wrapper whose outer element we can't add attributes to directly — its
//      closest [id^="shopify-section-"] ancestor becomes the container. (Native section
//      wrappers get a template-prefixed DOM id at render time, e.g.
//      "shopify-section-template--123__section_abc", so a hardcoded id would silently
//      never match — this sidesteps that entirely.)
//
// Within a container, elements matching NUMBER_SELECTOR are used if any exist (lets a
// section scope precisely to its own "number" elements and ignore unrelated numbers in
// body copy); otherwise every leaf element (no element children) is checked. Either way,
// only elements whose trimmed text is purely "<digits>" or "<digits>%" are animated —
// everything else (suffix labels like "min" or "-in-") is left untouched.
//
// Merchant-facing on/off switch: Theme settings → Animations → "Animate stat numbers on
// scroll" (settings.stats_counter_animation_enabled), rendered as a data attribute on
// <main id="MainContent"> in layout/theme.liquid.
import { getIntersectionRoot } from '@theme/scroll-container';

(function () {
  var DURATION = 1400;
  var NUMBER_SELECTOR = '.stats-citations__number-part--big';
  var DEFAULT_LEAF_SELECTOR = 'h1, h2, h3, p, span, strong, b';

  function animate(el, target, suffix) {
    var start = null;
    function step(timestamp) {
      if (start === null) start = timestamp;
      var progress = Math.min((timestamp - start) / DURATION, 1);
      var eased = 1 - Math.pow(1 - progress, 3);
      el.textContent = Math.round(eased * target) + suffix;
      if (progress < 1) window.requestAnimationFrame(step);
    }
    window.requestAnimationFrame(step);
  }

  function collectStats(container) {
    var stats = [];
    var specific = container.querySelectorAll(NUMBER_SELECTOR);
    var nodes = specific.length ? specific : container.querySelectorAll(DEFAULT_LEAF_SELECTOR);

    nodes.forEach(function (node) {
      if (node.children.length > 0) return;
      var match = node.textContent.trim().match(/^(\d+)(%?)$/);
      if (!match) return;
      stats.push({ el: node, target: parseInt(match[1], 10), suffix: match[2] });
    });
    return stats;
  }

  function initContainer(container) {
    if (container.dataset.counterInitialized) return;
    container.dataset.counterInitialized = 'true';

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var stats = collectStats(container);
    if (!stats.length) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          stats.forEach(function (stat) {
            animate(stat.el, stat.target, stat.suffix);
          });
          observer.disconnect();
        });
      },
      { root: getIntersectionRoot(), threshold: 0.4 }
    );
    observer.observe(container);
  }

  function init() {
    var main = document.getElementById('MainContent');
    if (main && main.dataset.statsCounterAnimationEnabled === 'false') return;

    document.querySelectorAll('[data-counter-scope]').forEach(initContainer);
    document.querySelectorAll('[data-counter-marker]').forEach(function (marker) {
      var container = marker.closest('[id^="shopify-section-"]');
      if (container) initContainer(container);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
